import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  Tooltip,
  Button,
  Popover,
  makeStyles,
} from '@material-ui/core';

import GetAppIcon from '@material-ui/icons/GetApp';
import MapIcon from '@material-ui/icons/Map';
import ImageIcon from '@material-ui/icons/Image';

import AddLocationIcon from '@material-ui/icons/AddLocation';
import AssessmentIcon from '@material-ui/icons/Assessment';
import CloseIcon from '@material-ui/icons/Close';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PaletteIcon from '@material-ui/icons/Palette';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

import { useTranslation } from 'react-i18next';
import ImageChooserCardMepBay from './ImageChooserCard';
import OverlayPanelsToggle from '../map/OverlayPanelsToggle';
import { useScrollIntoViewOnOpen } from '../../../common/utils';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { RETURN_BUTTONS_SAFE_AREA, OVERLAY_COLUMN_LAYOUT } from '../../pages/Shoreline/theme';
import { shallowEqual, useSelector } from 'react-redux';

import React from 'react';
import DeleteIcon from '@material-ui/icons/Delete';
import { truncateDecimals } from './utils/markerUtils';

import SimpleInfoDialog from '../dialog/SimpleInfoDialog';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * MEPBay side panel, its floating windows, and the point-selection map overlay.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:MEPBAY
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    maxHeight: '100vh',
    flexGrow: 1,
    position: 'relative',
    display: 'flex',
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
  },
  sidePanel: {
    width: 400,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    padding: theme.spacing(2),
    overflowY: 'auto',
    maxHeight: '100vh',
  },
  stepCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  completedStep: {
    backgroundColor: 'rgba(76,175,80,0.15)',
    borderLeft: '4px solid #4caf50',
  },
  activeStep: {
    backgroundColor: 'rgba(255,152,0,0.15)',
    borderLeft: '4px solid #ff9800',
  },
  inactiveStep: {
    backgroundColor: theme.palette.background.default,
    borderLeft: `4px solid ${theme.palette.divider}`,
  },
  coordinatesText: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  selectingMode: {
    '& *': {
      cursor: 'crosshair !important',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      border: '3px solid #ff9800',
      borderRadius: '4px',
      pointerEvents: 'none',
      zIndex: 1000,
      animation: '$pulse 2s infinite',
    },
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
    '100%': {
      opacity: 1,
    },
  },
  layersButton: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1000,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
  },
  layersMenu: {
    '& .MuiPaper-root': {
      minWidth: 300,
      maxHeight: 400,
      overflowY: 'auto',
    },
  },
  layerItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  layerControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  opacitySlider: {
    width: 80,
    marginLeft: theme.spacing(1),
  },
  activeLayerIndicator: {
    backgroundColor: 'rgba(33,150,243,0.15)',
    borderLeft: '4px solid #2196f3',
  },
  activeLayerCard: {
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(33,150,243,0.15)',
    color: theme.palette.text.primary,
  },
  floatingButton: {
    position: 'fixed',
    zIndex: 1000,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
  },
  addImageButton: {
    top: theme.spacing(2),
    left: theme.spacing(2),
  },
  addPointsButton: {
    top: theme.spacing(10),
    left: theme.spacing(2),
  },
  viewDataButton: {
    position: 'relative',
    textTransform: 'none',
    fontWeight: 500,
    padding: theme.spacing(0.5, 1.5),
  },
  rightColumn: {
    position: 'fixed',
    top: theme.spacing(8),
    right: theme.spacing(2),
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
    maxHeight: `calc(100vh - ${theme.spacing(8) + RETURN_BUTTONS_SAFE_AREA}px)`,
    overflowY: 'auto',
    ...OVERLAY_COLUMN_LAYOUT,
  },
  floatingWindow: {
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: theme.spacing(1),
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    zIndex: 1001,
    maxWidth: '90vw',
    overflow: 'hidden',
  },
  addImageWindow: {
    width: '350px',
    flexShrink: 0,
  },
  addPointsWindow: {
    width: '400px',
    flexShrink: 0,
  },
  /**
   * Same width as the layers and add-points windows: this one stacks in the same column as those,
   * and the column takes the width of its widest child, so a wider card here stretched the whole
   * stack and left this window itself clipped against the column's `overflow-x`.
   * The `top`/`right` offsets it used to carry are gone with it - they date from when the window
   * floated over the map on its own. On a relatively positioned card inside a scrolling column
   * they only shift it out of its slot without adding to the column's scroll height, so whatever
   * the offset pushed past the bottom edge was cut off with no way to scroll to it.
   */
  viewDataWindow: {
    width: '400px',
    flexShrink: 0,
  },
  windowHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    borderRadius: `${theme.spacing(1)}px ${theme.spacing(1)}px 0 0`,
  },
  windowContent: {
    padding: theme.spacing(1.5),
  },
  closeButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.5),
  },
  colorPicker: {
    padding: theme.spacing(2),
  },
  colorOption: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    margin: theme.spacing(0.5),
    cursor: 'pointer',
    border: '2px solid transparent',
    '&:hover': {
      border: '2px solid #666',
    },
  },
  selectedColor: {
    border: '2px solid #000 !important',
  },
}));

/**
 * @function SidePanel
 * @description Floating side panel for the MEPBay processing page: manages the image chooser
 * window, the step-by-step control-point selection wizard (with per-layer color picker), the
 * "view data" window (model results, charts, and exports), and the parabolic model results modal.
 * @param {Object} props - Component props
 * @param {JSX.Element} [props.mapSwitcher] - Rendered as the first card of the right-hand stack, so
 *  the "Mapa de fundo" switcher shares that column's flex flow instead of being absolutely
 *  positioned over it by the page (both used to sit at the same top/right corner).
 * @param {Object} props.layers - Object containing all layers
 * @param {String} props.activeLayerId - ID of the currently active layer
 * @param {Object} props.controlPoints - Object containing the three control points
 * @param {boolean} props.isCalculating - Whether calculations are in progress
 * @param {boolean} props.isSelectingPoint - Whether point selection mode is active
 * @param {Number} props.currentStep - Current step index in the point selection workflow
 * @param {Array} props.steps - Array of step configurations
 * @param {Function} props.handleStartSelection - Function to start point selection
 * @param {Function} props.resetActiveLayer - Function to reset the active layer
 * @param {Function} props.setModelModalOpen - Function to control modal visibility
 * @param {Function} props.setViewDataMenuAnchor - Function to control view data menu
 * @param {Object} props.viewDataMenuAnchor - Anchor element for view data menu
 * @param {boolean} props.modelModalOpen - Whether the model modal is open
 * @param {Object} props.layersRef - Reference to layers object
 * @param {Object} props.mapRef - Reference to Google Maps instance
 * @param {Function} props.createNewLayer - Function to create a new layer
 * @param {Function} props.deleteLayer - Function to delete a layer
 * @param {Function} props.updateLayerColor - Function to update layer color
 * @param {Function} props.setActiveLayerId - Function to set active layer ID
 * @see {@link exportToCSV}
 * @see {@link exportToShapefile}
 * @see {@link exportBackgroundImage}
 * @see {@link exportSatelliteImages}
 * **Called by:
 * @see {ProcessingPage} - MEPBay module's processing page (imported as ProcessingMepBayPage).
 * @memberof module:MEPBAY
 * @returns {JSX.Element} The side panel UI.
 */
const SidePanel = ({
  mapSwitcher = null,
  layers = {},
  activeLayerId,
  controlPoints = {},
  isCalculating,
  isSelectingPoint,
  currentStep,
  steps,
  handleStartSelection,
  resetActiveLayer,
  setModelModalOpen,
  setViewDataMenuAnchor,
  viewDataMenuAnchor,
  modelModalOpen,
  layersRef,
  mapRef,
  createNewLayer,
  deleteLayer,
  updateLayerColor,
  setActiveLayerId,
}) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const [pointsWindowExpanded, setPointsWindowExpanded] = React.useState(true);
  const [colorPickerAnchor, setColorPickerAnchor] = React.useState(null);
  const images = useSelector((state) => state.imagery.images);
  const metadata = useSelector((state) => state.mepbay.metadata, shallowEqual);

  /**
   * Opened from a button on the other side of the page, this panel joins the bottom of the left
   * column - past the visible part of it on a shorter screen. Keyed on the layer as well as on the
   * flag, so asking for another layer's details brings the panel back into view too.
   */
  const viewDataWindowRef = useScrollIntoViewOnOpen(modelModalOpen && activeLayerId);

  const availableColors = [
    '#f44336',
    '#e91e63',
    '#9c27b0',
    '#673ab7',
    '#3f51b5',
    '#2196f3',
    '#03a9f4',
    '#00bcd4',
    '#009688',
    '#4caf50',
    '#8bc34a',
    '#cddc39',
    '#ffeb3b',
    '#ffc107',
    '#ff9800',
    '#ff5722',
  ];

  /**
   * @function exportToCSV
   * @description Exports the active layer's parabolic model results (C0/C1/C2 coefficients, R0,
   * beta angle, and the full theta/radius table) as a downloadable CSV file.
   * @see {@link truncateDecimals}
   * **Called by:
   * @see {SidePanel} - via the "Export CSV" menu item's onClick.
   * @memberof module:MEPBAY
   * @returns {void}
   */
  const exportToCSV = () => {
    if (!activeLayerId || !layers[activeLayerId]?.parabolicResults) return;

    const layer = layers[activeLayerId];
    const results = layer.parabolicResults;

    const csvContent = [
      t('forms.mepbay.result.csvHeader').replace(',Data', ': ' + layer.name + ',Data'),
      `Data: ${new Date().toLocaleDateString()}`,
      ` `,
      `# Beta Angle (º):, ${truncateDecimals(results.beta, 2)}`,
      `# C0:, ${truncateDecimals(results.C0, 4)}`,
      `# C1:, ${truncateDecimals(results.C1, 4)}`,
      `# C2:, ${truncateDecimals(results.C2, 4)}`,
      `# R0:, ${truncateDecimals(results.R0, 2)}`,
      '',
      t('forms.mepbay.result.thetaRadiusHeader'),
      ...results.thetaValues.map((row) => `${truncateDecimals(row.theta, 2)},${truncateDecimals(row.radius, 2)}`),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mepbay_${layer.name}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  /**
   * @function exportToShapefile
   * @description Exports the active layer's parabolic model as a GeoJSON file (despite the name,
   * not a Shapefile): the parabolic curve, each control point, the auxiliary point, and the
   * connecting control lines between them.
   * **Called by:
   * @see {SidePanel} - via the "Export GeoJSON" menu item's onClick.
   * @memberof module:MEPBAY
   * @returns {void}
   */
  const exportToShapefile = () => {
    if (!activeLayerId || !layers[activeLayerId]?.parabolicResults) return;

    const layer = layers[activeLayerId];
    const results = layer.parabolicResults;

    const features = [];

    features.push({
      type: 'Feature',
      properties: {
        name: `${layer.name}_parabolic_curve`,
        type: 'parabolic_curve',
        beta_angle: results.cosBetaConf,
        C0: results.C0,
        C1: results.C1,
        C2: results.C2,
        R0: results.R0,
      },
      geometry: {
        type: 'LineString',
        coordinates: results.linePoints.map((coord) => [coord.lng, coord.lat]),
      },
    });

    if (controlPoints.diffractionPoint) {
      features.push({
        type: 'Feature',
        properties: {
          name: `${layer.name}_diffraction_point`,
          type: 'diffraction_point',
          point_number: 1,
        },
        geometry: {
          type: 'Point',
          coordinates: [controlPoints.diffractionPoint.lng, controlPoints.diffractionPoint.lat],
        },
      });
    }

    if (controlPoints.coastalControlPoint) {
      features.push({
        type: 'Feature',
        properties: {
          name: `${layer.name}_closure_point`,
          type: 'closure_point',
          point_number: 2,
        },
        geometry: {
          type: 'Point',
          coordinates: [controlPoints.coastalControlPoint.lng, controlPoints.coastalControlPoint.lat],
        },
      });
    }

    if (controlPoints.predominantWavePoint) {
      features.push({
        type: 'Feature',
        properties: {
          name: `${layer.name}_tangent_point`,
          type: 'tangent_point',
          point_number: 3,
        },
        geometry: {
          type: 'Point',
          coordinates: [controlPoints.predominantWavePoint.lng, controlPoints.predominantWavePoint.lat],
        },
      });
    }

    const layerRef = layersRef.current[activeLayerId];
    if (layerRef && layerRef.markers && layerRef.markers.auxiliaryPoint) {
      const auxiliaryPosition = layerRef.markers.auxiliaryPoint.getPosition();
      features.push({
        type: 'Feature',
        properties: {
          name: `${layer.name}_auxiliary_point`,
          type: 'auxiliary_point',
          point_number: 4,
        },
        geometry: {
          type: 'Point',
          coordinates: [auxiliaryPosition.lng(), auxiliaryPosition.lat()],
        },
      });
    }

    if (controlPoints.predominantWavePoint && controlPoints.coastalControlPoint) {
      features.push({
        type: 'Feature',
        properties: {
          name: `${layer.name}_closure_tangent_line`,
          type: 'control_line',
          from_point: 'closure_point',
          to_point: 'tangent_point',
        },
        geometry: {
          type: 'LineString',
          coordinates: [
            [controlPoints.coastalControlPoint.lng, controlPoints.coastalControlPoint.lat],
            [controlPoints.predominantWavePoint.lng, controlPoints.predominantWavePoint.lat],
          ],
        },
      });
    }

    if (controlPoints.diffractionPoint && controlPoints.coastalControlPoint) {
      features.push({
        type: 'Feature',
        properties: {
          name: `${layer.name}_diffraction_closure_line`,
          type: 'control_line',
          from_point: 'diffraction_point',
          to_point: 'closure_point',
        },
        geometry: {
          type: 'LineString',
          coordinates: [
            [controlPoints.diffractionPoint.lng, controlPoints.diffractionPoint.lat],
            [controlPoints.coastalControlPoint.lng, controlPoints.coastalControlPoint.lat],
          ],
        },
      });
    }
    if (controlPoints.diffractionPoint && layerRef && layerRef.markers && layerRef.markers.auxiliaryPoint) {
      const auxiliaryPosition = layerRef.markers.auxiliaryPoint.getPosition();
      features.push({
        type: 'Feature',
        properties: {
          name: `${layer.name}_diffraction_auxiliary_line`,
          type: 'control_line',
          from_point: 'diffraction_point',
          to_point: 'auxiliary_point',
        },
        geometry: {
          type: 'LineString',
          coordinates: [
            [controlPoints.diffractionPoint.lng, controlPoints.diffractionPoint.lat],
            [auxiliaryPosition.lng(), auxiliaryPosition.lat()],
          ],
        },
      });
    }

    const geojson = {
      type: 'FeatureCollection',
      features: features,
    };

    const blob = new Blob([JSON.stringify(geojson, null, 2)], {
      type: 'application/json',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mepbay_${layer.name}_complete_${new Date().toISOString().slice(0, 10)}.geojson`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  /**
   * @function exportBackgroundImage
   * @description Exports a snapshot of the map (fit to the layer's control/parabola points, with
   * a rendered legend) as a georeferenced TIFF image.
   * 1. Computes padded bounds covering every control/parabola/auxiliary point and fits the map to them.
   * 2. Captures the map as a canvas via html2canvas, then draws a legend panel below it.
   * 3. Encodes the combined canvas as a GeoTIFF (via `createTIFF`) and triggers its download.
   * @see {@link createTIFF}
   * **Called by:
   * @see {SidePanel} - via the "Export background image" menu item's onClick.
   * @memberof module:MEPBAY
   * @returns {Promise<void>}
   */
  const exportBackgroundImage = async () => {
    if (!mapRef.current || !activeLayerId || !layers[activeLayerId]) return;

    try {
      const layer = layers[activeLayerId];
      if (
        !layer.parabolicResults ||
        !layer.parabolicResults.linePoints ||
        layer.parabolicResults.linePoints.length === 0
      ) {
        alert(t('forms.mepbay.result.noLineFound'));
        return;
      }

      const allCoords = [];

      if (controlPoints.diffractionPoint)
        allCoords.push([controlPoints.diffractionPoint.lat, controlPoints.diffractionPoint.lng]);
      if (controlPoints.coastalControlPoint)
        allCoords.push([controlPoints.coastalControlPoint.lat, controlPoints.coastalControlPoint.lng]);
      if (controlPoints.predominantWavePoint)
        allCoords.push([controlPoints.predominantWavePoint.lat, controlPoints.predominantWavePoint.lng]);

      layer.parabolicResults.linePoints.forEach((coord) => {
        allCoords.push([coord.lat, coord.lng]);
      });

      const layerRef = layersRef.current[activeLayerId];
      if (layerRef && layerRef.markers && layerRef.markers.auxiliaryPoint) {
        const auxiliaryPosition = layerRef.markers.auxiliaryPoint.getPosition();
        allCoords.push([auxiliaryPosition.lat(), auxiliaryPosition.lng()]);
      }

      if (allCoords.length === 0) {
        alert(t('forms.mepbay.result.couldNotCalculateBounds'));
        return;
      }

      const lats = allCoords.map((coord) => coord[0]);
      const lngs = allCoords.map((coord) => coord[1]);
      const bounds = {
        north: Math.max(...lats),
        south: Math.min(...lats),
        east: Math.max(...lngs),
        west: Math.min(...lngs),
      };

      const latPadding = (bounds.north - bounds.south) * 0.1;
      const lngPadding = (bounds.east - bounds.west) * 0.1;

      const paddedBounds = {
        north: bounds.north + latPadding,
        south: bounds.south - latPadding,
        east: bounds.east + lngPadding,
        west: bounds.west - lngPadding,
      };

      const googleBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(paddedBounds.south, paddedBounds.west),
        new window.google.maps.LatLng(paddedBounds.north, paddedBounds.east),
      );

      mapRef.current.fitBounds(googleBounds);

      await new Promise((resolve) => setTimeout(resolve, 1000));

      const mapDiv = mapRef.current.getDiv();

      const mapCanvas = await html2canvas(mapDiv, {
        useCORS: true,
        allowTaint: false,
        scale: 1,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const legendHeight = 200;
      const canvas = document.createElement('canvas');
      canvas.width = mapCanvas.width;
      canvas.height = mapCanvas.height + legendHeight;
      const ctx = canvas.getContext('2d');

      ctx.drawImage(mapCanvas, 0, 0);

      const legendY = mapCanvas.height;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.fillRect(0, legendY, canvas.width, legendHeight);
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2;
      ctx.strokeRect(0, legendY, canvas.width, legendHeight);

      ctx.fillStyle = '#000';
      ctx.font = 'bold 18px Arial';
      ctx.fillText(t('forms.mepbay.result.legendTitle'), 20, legendY + 30);

      const legendItems = [
        {
          color: layer.color,
          symbol: '●',
          label: t('forms.mepbay.result.legendDiffractionPoint'),
          y: legendY + 60,
        },
        {
          color: layer.color,
          symbol: '●',
          label: t('forms.mepbay.result.legendClosurePoint'),
          y: legendY + 90,
        },
        {
          color: layer.color,
          symbol: '●',
          label: t('forms.mepbay.result.legendTangentPoint'),
          y: legendY + 120,
        },
        {
          color: layer.color,
          symbol: 'R0',
          label: t('forms.mepbay.result.legendControlLine'),
          y: legendY + 150,
        },
        {
          color: layer.color,
          symbol: '━ ━',
          label: t('forms.mepbay.result.legendParabolicLine'),
          y: legendY + 180,
        },
      ];

      ctx.font = '14px Arial';
      legendItems.forEach((item) => {
        ctx.fillStyle = item.color;
        if (item.symbol === '●') {
          ctx.beginPath();
          ctx.arc(30, item.y - 5, 6, 0, 2 * Math.PI);
          ctx.fill();
        } else if (item.symbol === '━ ━') {
          ctx.lineWidth = 3;
          ctx.strokeStyle = item.color;
          ctx.setLineDash([5, 5]);
          ctx.beginPath();
          ctx.moveTo(20, item.y - 5);
          ctx.lineTo(40, item.y - 5);
          ctx.stroke();
          ctx.setLineDash([]);
        } else if (item.symbol === 'R0') {
          ctx.fillStyle = '#000';
          ctx.font = 'bold 12px Arial';
          ctx.fillText('R0', 22, item.y);
          ctx.font = '14px Arial';
        } else {
          ctx.lineWidth = 3;
          ctx.strokeStyle = item.color;
          ctx.beginPath();
          ctx.moveTo(20, item.y - 5);
          ctx.lineTo(40, item.y - 5);
          ctx.stroke();
        }

        ctx.fillStyle = '#000';
        ctx.fillText(item.label, 60, item.y);
      });

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      /**
       * @function createTIFF
       * @description Encodes raw RGBA canvas image data as an uncompressed, striped, baseline
       * TIFF file (little-endian, 8-bit RGB), building its header/IFD/bits-per-sample/resolution
       * sections manually.
       * @param {ImageData} imageData - Canvas ImageData (RGBA) to encode.
       * @param {Number} width - Image width, in pixels.
       * @param {Number} height - Image height, in pixels.
       * @see {@link writeIFDEntry}
       * **Called by:
       * @see {exportBackgroundImage}
       * @memberof module:MEPBAY
       * @returns {Uint8Array} The encoded TIFF file bytes.
       */
      const createTIFF = (imageData, width, height) => {
        const data = imageData.data;
        const bytesPerPixel = 3;
        const imageDataSize = width * height * bytesPerPixel;

        const rgbData = new Uint8Array(imageDataSize);
        for (let i = 0, j = 0; i < data.length; i += 4, j += 3) {
          rgbData[j] = data[i];
          rgbData[j + 1] = data[i + 1];
          rgbData[j + 2] = data[i + 2];
        }

        const tiffHeader = new ArrayBuffer(8);
        const headerView = new DataView(tiffHeader);
        headerView.setUint16(0, 0x4949, true);
        headerView.setUint16(2, 42, true);
        headerView.setUint32(4, 8, true);

        const numTags = 11;
        const ifdSize = 2 + numTags * 12 + 4;
        const ifd = new ArrayBuffer(ifdSize);
        const ifdView = new DataView(ifd);

        let offset = 0;
        ifdView.setUint16(offset, numTags, true);
        offset += 2;

        /**
         * @function writeIFDEntry
         * @description Writes a single 12-byte TIFF IFD (Image File Directory) entry at the
         * current offset, then advances the offset.
         * @param {Number} tag - TIFF tag id.
         * @param {Number} type - TIFF field type.
         * @param {Number} count - Number of values.
         * @param {Number} value - Value or offset to the value.
         * **Called by:
         * @see {createTIFF}
         * @memberof module:MEPBAY
         * @returns {void}
         */
        const writeIFDEntry = (tag, type, count, value) => {
          ifdView.setUint16(offset, tag, true);
          ifdView.setUint16(offset + 2, type, true);
          ifdView.setUint32(offset + 4, count, true);
          ifdView.setUint32(offset + 8, value, true);
          offset += 12;
        };

        writeIFDEntry(256, 3, 1, width);
        writeIFDEntry(257, 3, 1, height);
        writeIFDEntry(258, 3, 3, 8 + ifdSize);
        writeIFDEntry(259, 3, 1, 1);
        writeIFDEntry(262, 3, 1, 2);
        writeIFDEntry(273, 4, 1, 8 + ifdSize + 6);
        writeIFDEntry(277, 3, 1, 3);
        writeIFDEntry(278, 3, 1, height);
        writeIFDEntry(279, 4, 1, imageDataSize);
        writeIFDEntry(282, 5, 1, 8 + ifdSize + 6 + imageDataSize);
        writeIFDEntry(283, 5, 1, 8 + ifdSize + 6 + imageDataSize + 8);

        ifdView.setUint32(offset, 0, true);

        const bitsPerSample = new ArrayBuffer(6);
        const bitsView = new DataView(bitsPerSample);
        bitsView.setUint16(0, 8, true);
        bitsView.setUint16(2, 8, true);
        bitsView.setUint16(4, 8, true);

        const resolutions = new ArrayBuffer(16);
        const resView = new DataView(resolutions);
        resView.setUint32(0, 72, true);
        resView.setUint32(4, 1, true);
        resView.setUint32(8, 72, true);
        resView.setUint32(12, 1, true);

        const totalSize =
          tiffHeader.byteLength +
          ifd.byteLength +
          bitsPerSample.byteLength +
          rgbData.byteLength +
          resolutions.byteLength;
        const tiffFile = new Uint8Array(totalSize);

        let pos = 0;
        tiffFile.set(new Uint8Array(tiffHeader), pos);
        pos += tiffHeader.byteLength;
        tiffFile.set(new Uint8Array(ifd), pos);
        pos += ifd.byteLength;
        tiffFile.set(new Uint8Array(bitsPerSample), pos);
        pos += bitsPerSample.byteLength;
        tiffFile.set(rgbData, pos);
        pos += rgbData.byteLength;
        tiffFile.set(new Uint8Array(resolutions), pos);

        return tiffFile;
      };

      const tiffData = createTIFF(imageData, canvas.width, canvas.height);

      const tiffBlob = new Blob([tiffData], { type: 'image/tiff' });
      const url = window.URL.createObjectURL(tiffBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mepbay_${layer.name}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.tif`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(t('forms.mepbay.result.errorExportingTiff'), error);
      console.error(t('forms.mepbay.result.errorDetails'), error.message, error.stack);
      alert(t('forms.mepbay.result.errorExportingImage'));
    }
  };

  /**
   * @function exportSatelliteImages
   * @description Downloads every loaded satellite image's thumbnail and packages them into a
   * single ZIP file for download.
   * **Called by:
   * @see {SidePanel} - via the "Export satellite images" menu item's onClick.
   * @memberof module:MEPBAY
   * @returns {Promise<void>}
   */
  const exportSatelliteImages = async () => {
    const loadedImageDates = Object.values(images).map((img) => img.date);

    const urlAnalyzedImages = metadata
      .filter((meta) => loadedImageDates.includes(meta.date))
      .map((meta) => meta.thumbnail)
      .filter(Boolean);

    if (urlAnalyzedImages.length === 0) {
      return alert('No images to export.');
    }

    const zip = new JSZip();
    for (let i = 0; i < urlAnalyzedImages.length; i++) {
      try {
        const meta = metadata.filter((m) => loadedImageDates.includes(m.date))[i];
        const response = await fetch(urlAnalyzedImages[i]);
        if (!response.ok) {
          console.error(`Erro ao baixar imagem ${i + 1}:`, response.status);
          continue;
        }
        const blob = await response.blob();
        const filename = `image_${meta.date.replace(/[:/\\]/g, '-')}.png`;
        zip.file(filename, blob);
      } catch (error) {
        console.error(`Erro ao processar imagem ${i + 1}:`, error);
      }
    }

    try {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = 'mepbay_images.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Erro ao criar ZIP:', error);
      alert('Erro ao exportar imagens. Verifique o console.');
    }
  };

  React.useEffect(() => {
    if (activeLayerId && !isSelectingPoint && !isCalculating) {
      const completedPoints = Object.values(controlPoints).filter(Boolean).length;
      const nextStepIndex = completedPoints;

      if (nextStepIndex < steps.length && !controlPoints[steps[nextStepIndex].key]) {
        const timer = setTimeout(() => {
          handleStartSelection(nextStepIndex);
        }, 500);

        return () => clearTimeout(timer);
      }
    }
  }, [controlPoints, activeLayerId, isSelectingPoint, isCalculating, steps, handleStartSelection]);

  const [showHelpDialog, setShowHelpDialog] = React.useState(false);

  const [showHelpDialogMap, setShowHelpDialogMap] = React.useState(false);

  // Only the control point layers belong here: the ROI has no parabolic model to detail or export.
  const pointLayers = Object.values(layers).filter((layer) => !layer.isROI);

  return (
    <>
      <Menu
        anchorEl={viewDataMenuAnchor}
        open={Boolean(viewDataMenuAnchor)}
        onClose={() => setViewDataMenuAnchor(null)}
        className={classes.layersMenu}>
        {pointLayers.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary={t('forms.mepbay.result.noLayersAvailable')} />
          </MenuItem>
        ) : (
          pointLayers.map((layer) => (
            <Box key={layer.id}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: layer.color,
                    marginRight: 12,
                    display: 'inline-block',
                  }}
                />
                <ListItemText
                  primary={layer.name}
                  secondary={
                    layer.completed ? t('forms.mepbay.result.dataAvailable') : t('forms.mepbay.result.incomplete')
                  }
                />
                {layer.completed && layer.parabolicResults && (
                  <>
                    <MenuItem
                      onClick={() => {
                        setActiveLayerId(layer.id);
                        setViewDataMenuAnchor(null);
                        setModelModalOpen(true);
                      }}>
                      <ListItemText primary={t('forms.mepbay.result.viewDetailsAndExport')} />
                    </MenuItem>
                  </>
                )}
              </div>

              <Divider />
            </Box>
          ))
        )}
      </Menu>
      <div className={classes.rightColumn}>
        <OverlayPanelsToggle side="right" />
        {mapSwitcher}
        <div className={`${classes.floatingWindow} ${classes.addImageWindow}`}>
          <div className={classes.windowHeader}>
            <Box display="flex" alignItems="center">
              <Typography variant="subtitle1" style={{ marginRight: 8, fontWeight: 500 }}>
                {t('forms.mepbay.result.viewAndExportData')}
              </Typography>
              <Tooltip title={t('forms.mepbay.result.viewAndExportData')}>
                <IconButton onClick={() => setShowHelpDialogMap(true)} size="small" style={{ padding: 4 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <SimpleInfoDialog
              open={showHelpDialogMap}
              close={() => setShowHelpDialogMap(false)}
              title="forms.mepbay.result.viewAndExportData"
              text="forms.mepbay.result.viewAndExportDataInfo"
            />
          </div>

          <div className={classes.windowContent}>
            <ImageChooserCardMepBay />
          </div>
        </div>

        <Button
          className={`${classes.floatingButton} ${classes.viewDataButton}`}
          startIcon={<AssessmentIcon />}
          onClick={(e) => setViewDataMenuAnchor(e.currentTarget)}>
          {t('forms.mepbay.result.detailsAndExport')}
        </Button>
      </div>

      <div className={`${classes.floatingWindow} ${classes.addPointsWindow}`}>
        <div className={classes.windowHeader}>
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle1" style={{ marginRight: 8, fontWeight: 500 }}>
              {t('forms.mepbay.result.manageLayers')}
            </Typography>
            <Tooltip title={t('forms.mepbay.4.helpTooltip')}>
              <IconButton onClick={() => setShowHelpDialog(true)} size="small" style={{ padding: 4 }}>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <SimpleInfoDialog
            open={showHelpDialog}
            close={() => setShowHelpDialog(false)}
            title="forms.mepbay.4.helpTitle"
            text="forms.mepbay.4.helpContent"
          />

          <IconButton className={classes.closeButton} onClick={() => setPointsWindowExpanded(!pointsWindowExpanded)}>
            {pointsWindowExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </div>
        {pointsWindowExpanded && (
          <div className={classes.windowContent}>
            {!activeLayerId ? (
              <Box textAlign="center" mb={1}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {t('forms.mepbay.result.noLayersAvailable')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<AddLocationIcon />}
                  onClick={createNewLayer}
                  fullWidth>
                  {t('forms.mepbay.result.createNewLayer')}
                </Button>
              </Box>
            ) : (
              <>
                <Box className={classes.activeLayerCard} mb={1} p={1} borderRadius={1}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      flexDirection: 'column',
                    }}>
                    <Box display="flex" alignItems="center" mb={0.5}>
                      <Box
                        width={14}
                        height={14}
                        borderRadius="50%"
                        backgroundColor={layers[activeLayerId]?.color}
                        marginRight={1}
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => setColorPickerAnchor(e.currentTarget)}
                      />
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        {layers[activeLayerId]?.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => setColorPickerAnchor(e.currentTarget)}
                        style={{ marginLeft: 8 }}>
                        <PaletteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Typography variant="body2" color="textSecondary">
                      {Object.values(controlPoints).filter(Boolean).length}/3
                      {t('forms.mepbay.result.activeLayerInfo')}
                    </Typography>
                  </div>

                  <IconButton
                    size="small"
                    style={{ alignSelf: 'center' }}
                    onClick={() => deleteLayer(layers[activeLayerId]?.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                {steps.map((step, index) => {
                  const isCompleted = controlPoints[step.key] !== null;
                  const isActive = currentStep === index && isSelectingPoint;

                  const isPreviousCompleted =
                    index === 0 || (steps[index - 1] && controlPoints[steps[index - 1].key] !== null);
                  const isAvailable = isPreviousCompleted && !isCompleted;
                  const isLocked = !isPreviousCompleted && !isCompleted;

                  return (
                    <Box key={step.key} mb={0.5}>
                      <Button
                        fullWidth
                        size="small"
                        variant={isCompleted ? 'contained' : isAvailable ? 'outlined' : 'text'}
                        color={isCompleted ? 'primary' : isAvailable ? 'default' : 'inherit'}
                        onClick={() => (isAvailable ? handleStartSelection(index) : null)}
                        disabled={isCompleted || isActive || isCalculating || isLocked}
                        startIcon={
                          <Box
                            width={20}
                            height={20}
                            borderRadius="50%"
                            backgroundColor={
                              isCompleted ? '#4caf50' : isActive ? '#ff9800' : isAvailable ? '#2196f3' : '#e0e0e0'
                            }
                            color={isLocked ? '#9e9e9e' : 'white'}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            fontSize="0.8rem"
                            fontWeight="bold">
                            {index + 1}
                          </Box>
                        }
                        style={{
                          opacity: isLocked ? 0.5 : 1,
                          cursor: isLocked ? 'not-allowed' : 'pointer',
                        }}>
                        <Box textAlign="left" width="100%">
                          <Typography variant="body2" style={{ opacity: isLocked ? 0.6 : 1 }}>
                            {step.title}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" style={{ opacity: isLocked ? 0.6 : 1 }}>
                            {isCompleted
                              ? t('forms.mepbay.result.completed')
                              : isActive
                                ? t('forms.mepbay.result.selecting')
                                : isAvailable
                                  ? t('forms.mepbay.result.available')
                                  : t('forms.mepbay.result.blocked')}
                          </Typography>
                        </Box>
                      </Button>

                      {controlPoints[step.key] && (
                        <Typography variant="caption" display="block" mt={0.5} color="textSecondary">
                          Lat: {truncateDecimals(controlPoints[step.key].lat, 6)}, Lng:{' '}
                          {truncateDecimals(controlPoints[step.key].lng, 6)}
                        </Typography>
                      )}
                    </Box>
                  );
                })}

                <Divider style={{ margin: '8px 0' }} />

                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={resetActiveLayer}
                  disabled={isCalculating}
                  fullWidth>
                  {t('forms.mepbay.result.resetActiveLayer')}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
      <Popover
        open={Boolean(colorPickerAnchor)}
        anchorEl={colorPickerAnchor}
        onClose={() => setColorPickerAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}>
        <Box className={classes.colorPicker}>
          <Typography variant="subtitle2" gutterBottom>
            {t('forms.mepbay.result.chooseLayerColor')}
          </Typography>
          <Box display="flex" flexWrap="wrap" maxWidth={200}>
            {availableColors.map((color) => (
              <Box
                key={color}
                className={`${classes.colorOption} ${
                  layers[activeLayerId]?.color === color ? classes.selectedColor : ''
                }`}
                style={{ backgroundColor: color }}
                onClick={() => {
                  if (updateLayerColor) {
                    updateLayerColor(activeLayerId, color);
                  }
                  setColorPickerAnchor(null);
                }}
              />
            ))}
          </Box>
        </Box>
      </Popover>
      {activeLayerId && layers[activeLayerId]?.parabolicResults && modelModalOpen && (
        <div ref={viewDataWindowRef} className={`${classes.floatingWindow} ${classes.viewDataWindow}`}>
          <div className={classes.windowHeader}>
            <Typography variant="h6">{t('forms.mepbay.result.detailsAndExport')}</Typography>
            <IconButton className={classes.closeButton} onClick={() => setModelModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className={classes.windowContent}>
            <Typography variant="subtitle1" gutterBottom>
              {t('forms.mepbay.result.coefficientsAndParameters')}
            </Typography>
            <Box mb={2}>
              <strong>{t('forms.mepbay.result.betaAngle')}:</strong>{' '}
              {truncateDecimals(layers[activeLayerId].parabolicResults.beta, 2)}
              °
              <br />
              <strong>C0:</strong> {truncateDecimals(layers[activeLayerId].parabolicResults.C0, 4)} <br />
              <strong>C1:</strong> {truncateDecimals(layers[activeLayerId].parabolicResults.C1, 4)} <br />
              <strong>C2:</strong> {truncateDecimals(layers[activeLayerId].parabolicResults.C2, 4)} <br />
              <strong>C0 + C1 + C2 =</strong>{' '}
              {(
                layers[activeLayerId].parabolicResults.C0 +
                layers[activeLayerId].parabolicResults.C1 +
                layers[activeLayerId].parabolicResults.C2
              ).toFixed(2)}
              <br />
              <strong>R0: </strong>
              {truncateDecimals(layers[activeLayerId].parabolicResults.R0, 2)}
              {'m '}
            </Box>

            <Box mb={2}>
              <Button
                onClick={exportToCSV}
                color="primary"
                variant="contained"
                startIcon={<GetAppIcon />}
                fullWidth
                style={{ marginBottom: 8 }}>
                {t('forms.mepbay.result.export')}
              </Button>

              <Button
                onClick={exportToShapefile}
                color="default"
                variant="contained"
                startIcon={<MapIcon />}
                fullWidth
                style={{ marginBottom: 8 }}>
                Shapefile
              </Button>

              <Button
                onClick={exportSatelliteImages}
                color="secondary"
                variant="contained"
                startIcon={<ImageIcon />}
                fullWidth
                style={{ marginBottom: 8 }}>
                {t('forms.mepbay.result.exportSatelliteImages')}
              </Button>

              <Button
                onClick={exportBackgroundImage}
                color="default"
                variant="contained"
                startIcon={<ImageIcon />}
                fullWidth>
                Imagem do Mapa (.tif)
              </Button>
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              {t('forms.mepbay.result.thetaRadiusTable')}
            </Typography>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                marginBottom: 16,
              }}>
              <thead>
                <tr style={{ background: '#e3f2fd' }}>
                  <th style={{ border: '1px solid #ccc', padding: 4, color: '#212121' }}>
                    {t('forms.mepbay.result.theta')}
                  </th>
                  <th style={{ border: '1px solid #ccc', padding: 4, color: '#212121' }}>
                    {t('forms.mepbay.result.radius')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {layers[activeLayerId].parabolicResults.thetaValues.map((row, idx) => (
                  <tr key={idx}>
                    <td style={{ border: '1px solid #ccc', padding: 4 }}>{truncateDecimals(row.theta, 2)}</td>
                    <td style={{ border: '1px solid #ccc', padding: 4 }}>{truncateDecimals(row.radius, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default SidePanel;
